import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, ChevronDown } from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import styles from "../../_css/EscalationManagement.module.css";
import { getTicketById, updateTicketStatus, updateTicketDetails, generateRoleplayPdf } from "../../requests";
import { useDispatch } from "react-redux"
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js"
import { notifySuccess, notifyError } from "../../utils/helper.js"
import DataLoader from "../common/DataLoader.js"

const TicketDetails = () => {
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [reportIssue, setReportIssue] = useState("");
    const [internalNote, setInternalNote] = useState("");
    const [isHovered, setIsHovered] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [pdfGenerated, setPdfGenerated] = useState(false);
    const dispatch = useDispatch();

    const navigate = useNavigate();
    const location = useLocation();
    const dropdownRef = useRef(null);
    // console.log("Ticket location: ", location.state?.activeTab)
    const activeTab = location.state?.activeTab;
    // Status options with colors
    const STATUS_OPTIONS = [
        { label: "Open", color: "#3B82F6" },
        { label: "In Progress", color: "#FACC15" },
        { label: "Resolved", color: "#10B981" },
        { label: "Closed", color: "#6B7280" },
        { label: "Pending", color: "#F97316" },
    ];

    // Default empty ticket structure
    const emptyTicket = {
        id: "",
        type: "",
        category: "",
        status: "Open",
        statusColor: "#3B82F6",
        company: "",
        date: "",
        scenario: "",
        attachment: "",
        internalNote: "",
        history: []
    };

    // Fetch ticket data based on ID from navigation state or URL params
    useEffect(() => {
        const fetchTicketData = async () => {
            setLoading(true);
            dispatch(startLoader());

            try {
                const ticketId = location.state?.ticketId;
                const foundTicket = await getTicketById({ ticketId });

                if (foundTicket.success) {
                    setTicket(foundTicket.ticket);
                    setReportIssue(foundTicket.ticket?.scenario || "");
                    setInternalNote(foundTicket.ticket?.internalNote || "");
                } else {
                    notifyError("Ticket not found");
                    setTicket(emptyTicket);
                }
            } catch (error) {
                notifyError("Error fetching ticket details");
                setTicket(emptyTicket);
            } finally {
                setLoading(false);
                dispatch(stopLoader())
            }
        };

        fetchTicketData();
    }, [location, navigate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleStatusDropdown = () => {
        setShowStatusDropdown(!showStatusDropdown);
    };

    const handleGeneratePdf = async () => {
        if (!ticket?.roleplay_id || generatingPdf) return;

        setGeneratingPdf(true);
        dispatch(startLoader());

        try {
            const params = {
                roleplayId: ticket.roleplay_id
            };

            const pdfData = await generateRoleplayPdf(params); // this is arraybuffer
            console.log("PDF Data:", pdfData);

            // Convert arraybuffer → Blob
            const blob = new Blob([pdfData], { type: "application/pdf" });

            // Create temporary URL
            const pdfUrl = URL.createObjectURL(blob);

            // Open in new browser tab
            window.open(pdfUrl, "_blank");

            // SUCCESS UI updates
            setPdfGenerated(true);
            notifySuccess("PDF opened successfully!");

            // Add to history
            const updatedTicket = {
                ...ticket,
                history: [
                    {
                        date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
                        time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
                        status: "Roleplay PDF generated"
                    },
                    ...(ticket.history || [])
                ]
            };
            setTicket(updatedTicket);

        } catch (error) {
            console.error("Error generating PDF:", error);
            notifyError("Error generating PDF. Please try again.");
        } finally {
            setGeneratingPdf(false);
            dispatch(stopLoader());
        }
    };


    const handleStatusChange = async (newStatus) => {
        if (ticket && !updatingStatus) {
            setUpdatingStatus(true);
            dispatch(startLoader());
            setShowStatusDropdown(false);

            try {
                const statusOption = STATUS_OPTIONS.find(option => option.label === newStatus);
                const statusColor = statusOption ? statusOption.color : '#3B82F6';

                const ticketId = ticket.id;
                const status = newStatus;
                const updateResult = await updateTicketStatus({ ticketId, status });

                if (updateResult.data?.success) {
                    const updatedTicket = {
                        ...ticket,
                        status: newStatus,
                        statusColor: statusColor,
                        history: [
                            {
                                date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                                time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
                                status: `Status changed to ${newStatus}`
                            },
                            ...(ticket.history || [])
                        ]
                    };

                    setTicket(updatedTicket);
                    notifySuccess('Status updated successfully!');
                } else {
                    notifyError('Failed to update status');
                }
            } catch (error) {
                notifyError('Error updating status');
            } finally {
                setUpdatingStatus(false);
                dispatch(stopLoader());
            }
        }
    };

    const getStatusColor = (status) => {
        const statusOption = STATUS_OPTIONS.find(option => option.label === status);
        return statusOption ? statusOption.color : '#3B82F6';
    };

    const handleSave = async () => {
        if (ticket && !saving) {
            setSaving(true);
            dispatch(startLoader());

            try {
                const ticketId = ticket.id;
                const payload = {
                    ticketId,
                    scenario: reportIssue,
                    internalNote: internalNote
                };

                const updateResult = await updateTicketDetails(payload);

                if (updateResult.data?.success) {
                    const updatedTicket = {
                        ...ticket,
                        scenario: reportIssue,
                        internalNote: internalNote,
                        history: [
                            {
                                date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
                                time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
                                status: 'Internal note updated'
                            },
                            ...(ticket.history || [])
                        ]
                    };

                    setTicket(updatedTicket);
                    notifySuccess('Changes saved successfully!');
                } else {
                    notifyError('Failed to save changes');
                }
            } catch (error) {
                notifyError('Error saving changes');
            } finally {
                setSaving(false);
                dispatch(stopLoader());
            }
        }
    };

    if (loading) {
        return (
            <div className={styles.ticketDetailsContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button
                            onClick={() =>
                                navigate(`/escalation?tab=${activeTab}`)
                            }
                            className={styles.backButton}
                        >
                            <ArrowLeft size={20} color="#171717" />
                        </button>
                        <div>
                            <h1 className={styles.title}>Ticket Details</h1>
                        </div>
                    </div>
                </div>
                <div className={styles.loadingState}>
                    {/* <DataLoader /> */}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.ticketDetailsContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button
                        onClick={() => navigate(`/escalation?tab=${activeTab}`)}
                        className={styles.backButton}
                    >
                        <ArrowLeft size={20} color="#171717" />
                    </button>
                    <div>
                        <h1 className={styles.title}>
                            Ticket Details
                        </h1>
                    </div>
                </div>
            </div>

            <div className={styles.contentGrid}>
                {/* Left Column - Scenario and Details */}
                <div className={styles.leftColumn}>
                    <h1 className={styles.sectionTitle}>
                        {ticket.type}
                    </h1>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Reported Issue
                        </label>
                        <textarea
                            placeholder={ticket?.scenario ? ticket.scenario : "No data to preview"}
                            value={reportIssue}
                            onChange={(e) => setReportIssue(e.target.value)}
                            rows={3}
                            className={styles.textarea}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Attachments
                        </label>
                        <div className={styles.attachmentContainer}>
                            <input
                                type="text"
                                placeholder={ticket?.attachment ? ticket.attachment : "No data to preview"}
                                value={ticket?.attachment || ""}
                                readOnly
                                className={styles.attachmentInput}
                            />
                            <button
                                className={styles.viewButton}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                onClick={() => {
                                    if (ticket?.attachment) {
                                        window.open(ticket.attachment, "_blank"); // open in new tab
                                    }
                                }}
                                disabled={!ticket?.attachment}
                            >
                                View Image
                            </button>
                            {ticket.dataShared && (
                                <button
                                    className={styles.viewButton}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    onClick={handleGeneratePdf}
                                    disabled={!ticket?.roleplay_id || generatingPdf}
                                >
                                    {generatingPdf ? 'Generating...' : 'Data Pdf'}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Internal note
                        </label>
                        <textarea
                            placeholder={ticket?.internalNote ? ticket.internalNote : "No data to preview"}
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            rows={3}
                            className={styles.textarea}
                        />
                    </div>

                    <div>
                        <button
                            className={styles.saveButton}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>

                {/* Right Column - Status and History */}
                <div className={styles.rightColumn}>
                    <div className={styles.statusSection}>
                        <h1 className={styles.sectionTitle}>
                            Status
                        </h1>
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                            <button
                                onClick={toggleStatusDropdown}
                                className={styles.statusButton}
                                disabled={updatingStatus}
                            >
                                <span className={styles.statusContent}>
                                    <span
                                        className={styles.statusIndicator}
                                        style={{ backgroundColor: ticket?.statusColor || '#3B82F6' }}
                                    ></span>
                                    {updatingStatus ? 'Updating...' : ticket?.status || 'Open'}
                                </span>
                                <ChevronDown size={16} color="#6b7280" />
                            </button>

                            {showStatusDropdown && (
                                <div className={styles.ticketDetailsDropdown}>
                                    {STATUS_OPTIONS.map((statusOption, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => handleStatusChange(statusOption.label)}
                                            className={styles.ticketDetailsDropdownItem}
                                        >
                                            <span
                                                className={styles.statusIndicator}
                                                style={{ backgroundColor: statusOption.color }}
                                            ></span>
                                            {statusOption.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.historySection}>
                        <h1 className={styles.sectionTitle}>
                            History
                        </h1>
                        <div className={styles.historyTimeline}>
                            <div className={styles.timelineLine} />

                            {ticket?.history && ticket.history.length > 0 ? (
                                ticket.history.map((item, index) => (
                                    <div
                                        key={index}
                                        className={styles.historyItem}
                                    >
                                        <div className={styles.timelineDot} />
                                        <div className={styles.historyDate}>
                                            {item.date} &nbsp; {item.time}
                                        </div>
                                        <div className={styles.historyStatus}>
                                            {item.status}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className={styles.noData}>
                                    No data to preview
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketDetails;